import React, { useState, useEffect, useRef } from 'react';
import { Scale, Eye, EyeOff, Building, User, Mail, Lock, ArrowRight, Loader, AlertCircle, Users, Shield } from 'lucide-react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, TotpMultiFactorGenerator, getMultiFactorResolver, sendPasswordResetEmail, signOut, sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase-config';
import { getUserProfile, listMyPendingInvites, acceptInvitation } from '../services/userService';
import PendingInvitations from './PendingInvitations';
import { setRegistrationInProgress, holdRegistrationUntilActive } from '../services/authService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { trackLogin, trackLoginFailed, trackSignUp, trackFunnelStep } from '../services/analyticsService';

// Cloud Functions client — used by acceptFirmInvite (trusted server-side
// invite acceptance) and bootstrapNewFirm (trusted server-side new-firm
// bootstrap); see inviteAcceptanceService.js / functions/index.js for why
// these trust-sensitive multi-write sequences can't safely be a chain of
// direct client Firestore writes from here.
const functions = getFunctions();

// Map Firebase error codes to user-friendly messages
const friendlyError = (err) => {
  const code = err?.code || '';
  const map = {
    'auth/network-request-failed': 'Network error — check your internet connection and try again.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/email-already-in-use': 'An account with this email already exists. Try signing in instead.',
    'auth/weak-password': 'Password is too weak. Use at least 8 characters with uppercase, number, and special character.',
    'auth/user-disabled': 'This account has been disabled. Contact support.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Contact support.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
    'auth/internal-error': 'Something went wrong. Please try again in a moment.',
    'auth/invalid-verification-code': 'Invalid code. Check your authenticator app and try again.',
  };
  return map[code] || 'Something went wrong. Please try again.';
};

// Accidental-duplicate firm safeguard (Hales Solicitors incident). The server
// refuses — with a structured 'POSSIBLE_DUPLICATE_FIRM' — to mint a new firm
// for an account already connected to one (founded / team member / pending
// invite). Ask the user explicitly; only an explicit "yes" retries with
// confirmDuplicate. Firm names are not globally unique, so this is a
// confirmation, never a hard block.
export const callBootstrapNewFirm = async (bootstrapFn, payload, confirmFn = (m) => window.confirm(m)) => {
  try {
    return (await bootstrapFn(payload)).data;
  } catch (err) {
    if (err?.details?.code !== 'POSSIBLE_DUPLICATE_FIRM') throw err;
    const ok = confirmFn(`${err.message}\n\nIf you meant to join your existing firm, choose Cancel and ask its administrator to invite you.\n\nCreate a separate new firm anyway?`);
    if (!ok) throw Object.assign(new Error('New firm not created. If you meant to join your existing firm, ask its administrator to invite you.'), { code: 'cancelled' });
    return (await bootstrapFn({ ...payload, confirmDuplicate: true })).data;
  }
};

const Login = ({ onLoginSuccess, initialError, onDeliberateSignOut }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError || '');
  // Firm-move lifecycle: pending invitations awaiting an EXPLICIT choice
  // (removed / firmless sign-in, or a registration with several invitations).
  const [inviteChoice, setInviteChoice] = useState(null);
  // Neutral (non-error) guidance, e.g. "verify your email, then sign in".
  const [notice, setNotice] = useState('');

  // Surface a recovery error handed down from App.js (e.g. the auth
  // listener signing an incomplete/profile-less account back out — see
  // P0-4) even if Login was already mounted when it arrives.
  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [firmName, setFirmName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteDetected, setInviteDetected] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(false);
  // Incident 2026-11-03: the invite check runs per keystroke; only the latest
  // response may set the banner (a slow reply for a partial address must not
  // overwrite the reply for the full one).
  const inviteCheckSeq = useRef(0);
  // Founder path: the authoritative invitation check failed. Never treated as
  // "no invitation" — the person retries, or explicitly chooses to create a new
  // firm (the ref carries that informed choice into the resubmission).
  const [inviteCheckFailed, setInviteCheckFailed] = useState(false);
  const founderConfirmedNoInvite = useRef(false);

  // MFA states
  const [mfaStep, setMfaStep] = useState(null); // null | 'verify' (sign-in challenge)
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  // Password reset states
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);
    if (!resetEmail || !resetEmail.includes('@')) {
      setResetError('Please enter a valid email address.');
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') setResetError('No account found with this email address.');
      else if (err.code === 'auth/invalid-email') setResetError('Invalid email address.');
      else if (err.code === 'auth/too-many-requests') setResetError('Too many requests. Please try again later.');
      else setResetError('Failed to send reset email. Please try again.');
    }
    setResetLoading(false);
  };

  // C1 invite-takeover remediation (2026-10-07): an invitation is redeemed
  // only by an account that has VERIFIED the invited address (acceptFirmInvite
  // enforces this server-side). An unverified invitee gets a verification
  // link and is signed out with clear guidance; signing in again after
  // verifying offers the invitation (handleLogin's profile-less branch below).
  // Incident 2026-11-03: Login's own sign-outs are deliberate — tell the session
  // gate first so it ends the session at once instead of holding the null auth
  // event for its grace period (during which the refused users/{uid} listener
  // showed "We couldn't confirm your access" and Login, remounting, lost its notice).
  const deliberateSignOut = async () => {
    if (onDeliberateSignOut) onDeliberateSignOut();
    try { await signOut(auth); } catch (e) {}
  };

  const promptInviteEmailVerification = async (fbUser) => {
    // Incident 2026-10-22: tell the invitee exactly what happened — never claim a
    // link was sent when Firebase refused to send it — where it comes from, and
    // that the next step is SIGN IN (registering again cannot complete the join).
    let sendCode = null;
    try { await sendEmailVerification(fbUser); } catch (e) { sendCode = (e && e.code) || 'unknown'; console.error('[Invite] verification email failed:', sendCode); }
    await deliberateSignOut();
    if (!sendCode) {
      setNotice(`Almost there — we've sent a verification link to ${fbUser.email} (from noreply@firmassist-19248.firebaseapp.com; please check your spam folder too). Open it to confirm your email address, then come back and SIGN IN here — don't register again — to join your firm.`);
    } else if (sendCode === 'auth/too-many-requests') {
      setNotice(`We've already sent a verification link to ${fbUser.email} recently (from noreply@firmassist-19248.firebaseapp.com; check your spam folder too). Open that link, then SIGN IN here to join your firm.`);
    } else {
      setError(`We couldn't send the verification email to ${fbUser.email} just now. Please wait a few minutes, then sign in here again to receive a new link.`);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    // Profile-less invitee choosing among invitations: keep the session gate
    // in its registration wait until the explicit choice resolves.
    let holdRegistration = false;
    // Production incident (Michael Sarkodie / Amina Muniratu, 2026-09-24):
    // an EARLIER hypothesis in this file (the Functions SDK ContextProvider
    // possibly not having resolved its Auth reference yet) was disproven by
    // production evidence after deploying auth.authStateReady() below — the
    // real, confirmed root cause is a race with App.js's OWN independent
    // onAuthChange listener: that listener fires the instant
    // createUserWithEmailAndPassword/signInWithEmailAndPassword resolves,
    // and — since a brand-new registration's users/{uid} profile does not
    // exist yet at that exact moment (bootstrapNewFirm hasn't run yet) — it
    // was signing the user back out before THIS function ever reached
    // checkPendingInvite/bootstrapNewFirm, which is why those calls then
    // failed authenticated-user checks: the user was, by then, genuinely
    // signed out. See authService.js's isRegistrationInProgress for the fix.
    // authStateReady() is kept as a harmless, independently-correct practice
    // — it just was never the actual blocker here.
    setRegistrationInProgress(true);
    try {
      await auth.authStateReady();
      // Auto-retry with exponential backoff (common on mobile/African networks)
      // App Check / reCAPTCHA failures on mobile often resolve after a brief delay
      let userCredential;
      let lastErr;
      const maxAttempts = 3;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          userCredential = await signInWithEmailAndPassword(auth, email, password);
          break;
        } catch (retryErr) {
          lastErr = retryErr;
          const isNetworkErr = retryErr.code === 'auth/network-request-failed';
          const isInternalErr = retryErr.code === 'auth/internal-error';
          // Retry on network/internal errors (App Check failures surface as these)
          if ((isNetworkErr || isInternalErr) && attempt < maxAttempts - 1) {
            const delay = 2000 * (attempt + 1); // 2s, 4s
            console.log(`[Login] Attempt ${attempt + 1} failed (${retryErr.code}), retrying in ${delay}ms...`);
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          throw retryErr;
        }
      }
      if (!userCredential) throw lastErr;
      const fbUser = userCredential.user;
      // Same token-readiness fix as handleRegister above — a fresh sign-in
      // on a browser session Firestore hasn't initialized yet (IndexedDB
      // persistence + multi-tab leader election) can otherwise send the
      // very next Firestore read/write before a valid token is attached.
      await fbUser.getIdToken(true);
      trackLogin('email');
      const profileResult = await getUserProfile(fbUser.uid);
      // Registration reliability remediation: a permission-denied profile
      // read must NEVER be treated as "no profile exists" — the users/{uid}
      // read rule permits any authenticated caller to read their OWN doc,
      // so a denial here is genuinely anomalous, and proceeding as if this
      // were a brand-new user would mint an unwanted SECOND firm for an
      // existing user whose profile read merely failed transiently.
      if (!profileResult.success && profileResult.errorCode && profileResult.errorCode !== 'not-found') {
        console.error('[Login] profile lookup failed with a non-not-found error:', profileResult.errorCode);
        setError('We could not verify your account right now. Please try signing in again in a moment.');
        setLoading(false);
        return;
      }
      if (profileResult.success) {
        const profile = profileResult.user;
        if (profile.status === 'suspended') {
          setError('Your account has been suspended. Contact your firm administrator.');
          setLoading(false);
          return;
        }
        if (profile.status === 'removed' || !profile.firmId) {
          // Firm-move lifecycle (2026-10-05): a removed / firmless user with
          // pending invitations CHOOSES one explicitly (no first-invite
          // auto-acceptance). Acceptance runs through the trusted
          // acceptFirmInvite callable with expectedCurrentFirmId = null.
          const invitesResult = await listMyPendingInvites();
          if (!invitesResult.success) {
            console.error('[Login] invitation lookup failed:', invitesResult.code);
            setError('We could not check your invitation status. Please try again in a moment.');
            setLoading(false);
            return;
          }
          // M1: an unverified address sees no invitation details — verify first.
          if (invitesResult.emailVerificationRequired && !fbUser.emailVerified) { await promptInviteEmailVerification(fbUser); return; }
          if (invitesResult.invites.length > 0) {
            setInviteChoice({ invites: invitesResult.invites, fbUser, profile, displayName: profile.displayName || fbUser.displayName || '' });
            setLoading(false);
            return;
          } else {
            setError('Your account has been removed from the firm. Ask your admin to send a new invitation.');
            setLoading(false);
            return;
          }
        }
        // MFA configuration & enrolment (2026-10-06): the enrolment prompt is no
        // longer shown here (the session gate replaced it as soon as the account
        // reached ACTIVE). App's MFA gate (session/mfaPolicy.js +
        // components/MfaEnrollment.js) holds the shell instead.
        const loginData = {
          ...fbUser,
          firmId: profile.firmId,
          role: profile.role,
          permissions: profile.permissions,
          displayName: profile.displayName || fbUser.displayName,
          userProfile: profile
        };
        onLoginSuccess(loginData);
      } else {
        // C1 (2026-10-07): an invited account with no profile yet (e.g. an
        // invitee returning after verifying their email) joins through its
        // invitation — never by minting a new firm.
        const pendingResult = await listMyPendingInvites();
        if (!pendingResult.success) {
          console.error('[Login] invitation lookup failed:', pendingResult.code);
          setError('We could not check your invitation status. Please try again in a moment.');
          setLoading(false);
          return;
        }
        if (pendingResult.pendingCount > 0) {
          if (!fbUser.emailVerified) { await promptInviteEmailVerification(fbUser); return; }
          // Explicit choice only (firm-move lifecycle): the chooser accepts via
          // the acceptFirmInvite callable and handles a still-unverified email.
          holdRegistration = true;
          setInviteChoice({ invites: pendingResult.invites, fbUser, profile: null, displayName: fbUser.displayName || '' });
          setLoading(false);
          return;
        }
        // P0-4 / registration reliability remediation: this authenticated
        // user has no Firestore profile at all. Never let them fall through
        // with no error and no valid profile. Bootstrap now goes through
        // the same trusted, atomic, idempotent bootstrapNewFirm callable as
        // handleRegister's new-firm path (see its own header comment) —
        // it never leaves partial Firestore state, so there is no
        // "stranded" failure mode left to sign out and recover from here.
        let legacyBootstrap;
        try {
          const bootstrapFn = httpsCallable(functions, 'bootstrapNewFirm');
          const data = await callBootstrapNewFirm(bootstrapFn, {
            firmName: fbUser.displayName ? `${fbUser.displayName}'s Firm` : 'My Law Firm',
            fullName: fbUser.displayName || fbUser.email.split('@')[0],
          });
          legacyBootstrap = data;
        } catch (bootstrapErr) {
          console.error('[Login] legacy bootstrapNewFirm failed:', bootstrapErr.code, bootstrapErr.message);
          setError(bootstrapErr.message || 'We could not set up your account. Please try again — if this keeps happening, contact support.');
          setLoading(false);
          return;
        }
        // Incident 2026-10-21: keep the hold until the gate reaches ACTIVE (profile delivered).
        holdRegistration = true; holdRegistrationUntilActive();
        onLoginSuccess({
          ...fbUser,
          firmId: legacyBootstrap.firmId,
          role: legacyBootstrap.role || 'admin',
          permissions: {
            canManageCases: true, canManageClients: true, canManageBilling: true,
            canManageUsers: true, canViewAnalytics: true, canDeleteRecords: true,
            canManageDocuments: true, canManageFirmSettings: true
          },
          displayName: fbUser.displayName
        });
      }
    } catch (err) {
      console.error('Login error:', err.code, err.message);
      const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      trackLoginFailed(err.code || 'unknown', isMobile);
      // MFA required — user has TOTP enrolled
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
        setMfaStep('verify');
        setError('');
        setLoading(false);
        return;
      }
      if (err.code === 'auth/user-not-found') setError('No account found with this email.');
      else if (err.code === 'auth/wrong-password') setError('Incorrect password.');
      else if (err.code === 'auth/invalid-email') setError('Invalid email address.');
      else if (err.code === 'auth/invalid-credential') setError('Invalid email or password.');
      else if (err.code === 'auth/too-many-requests') setError('Too many attempts. Try again later.');
      else if (err.code === 'auth/network-request-failed' || err.code === 'auth/internal-error') {
        // On mobile, this is often caused by App Check / reCAPTCHA failure, not actual network issues
        const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
          setError('Connection issue on mobile. Try: (1) Open in your default browser (not in-app), (2) Clear browser cache, or (3) Switch between Wi-Fi and mobile data, then tap Sign In again.');
        } else {
          setError('Network error — check your internet connection and try again.');
        }
      }
      else setError(friendlyError(err));
    } finally {
      if (!holdRegistration) setRegistrationInProgress(false);
    }
    setLoading(false);
  };

// Pre-registration invite banner ("you've been invited") — this is the ONE
// genuinely UNAUTHENTICATED invite lookup in the app (a brand-new visitor,
// no account yet, still typing their email). Firestore rules cannot scope a
// direct list query for a caller with no auth token, so this goes through
// the checkInviteByEmail callable instead (P0-3 remediation — see
// firestore.rules' invites/{inviteId} comment) rather than the authenticated
// checkPendingInvite(email) Firestore query used everywhere else post-auth.
const checkInviteForEmail = async (emailToCheck) => {
    const seq = ++inviteCheckSeq.current;
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setInviteDetected(null);
      setCheckingInvite(false);
      return;
    }
    setCheckingInvite(true);
    let detected = null;
    try {
      const checkFn = httpsCallable(functions, 'checkInviteByEmail');
      const { data } = await checkFn({ email: emailToCheck });
      detected = data && data.invited ? { role: data.role } : null;
    } catch (err) {
      detected = null;
    }
    if (seq !== inviteCheckSeq.current) return; // superseded by a newer keystroke
    setInviteDetected(detected);
    setCheckingInvite(false);
  };

  // Registration reliability remediation (Phase 10 — observability): a
  // structured, PII-free stage log for the registration path specifically —
  // never logs password, ID token, or full email (domain only), so that a
  // future production failure tells us the STAGE and Firebase error CODE
  // without needing browser-console access from the affected user.
  const logRegistrationStage = (stage, extra = {}) => {
    const emailDomain = (email || '').split('@')[1] || 'unknown';
    console.log('[Registration]', stage, { emailDomain, ...extra });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    let holdRegistration = false; // see handleLogin
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(password)) { setError('Password must contain at least one uppercase letter.'); return; }
    if (!/[0-9]/.test(password)) { setError('Password must contain at least one number.'); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must contain at least one special character (!@#$%^&*).'); return; }
    // Firm name is validated after the server-side invite check below (invited users don't need one)
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    // Incident 2026-10-22: validate the founder's firm name BEFORE an Auth account
    // exists. Checking it only after createUserWithEmailAndPassword stranded a
    // profile-less account (then signed out with the setup error).
    if (!inviteDetected && !firmName.trim()) { setError('Please enter your firm name to create a new account.'); return; }
    setLoading(true);
    // Same isRegistrationInProgress() coordination as handleLogin above —
    // see authService.js's header comment for the confirmed root cause
    // (a race with App.js's independent onAuthChange listener, which
    // otherwise signs the user back out the instant Auth succeeds, before
    // this function ever reaches checkPendingInvite/bootstrapNewFirm).
    setRegistrationInProgress(true);
    try {
      await auth.authStateReady();
      let fbUser;

      // Try to create a new account
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        fbUser = userCredential.user;
        await updateProfile(fbUser, { displayName: fullName });
        // Production defect (Araba Chambers / Michael Sarkodie, 2026-09-24):
        // Firestore is initialized with persistentLocalCache +
        // persistentMultipleTabManager (firebase-config.js), which needs to
        // open IndexedDB and complete multi-tab leader election before it
        // can attach a fresh ID token to outgoing writes. On a brand-new
        // browser session this can still be settling when the VERY NEXT
        // line fires a Firestore call, so the first write after signup can
        // reach the server with no/stale auth and get rejected as
        // "Missing or insufficient permissions" — even though the rules
        // themselves correctly permit it once a real token is attached.
        // Forcing a token refresh here and awaiting it ensures Auth's own
        // state is fully settled before any Firestore call is issued.
        await fbUser.getIdToken(true);
        logRegistrationStage('auth_account_created', { uid: fbUser.uid, newAccount: true });
      } catch (createErr) {
        if (createErr.code === 'auth/email-already-in-use') {
          // Account already exists — try to sign them in instead. Whether
          // this is a fresh or existing account no longer changes what
          // happens below: the acceptFirmInvite callable and the P0-4
          // idempotency guard both key off the ACTUAL current Firestore
          // state (does a profile/firm already exist?), not which auth
          // branch got us here.
          try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            fbUser = userCredential.user;
            await updateProfile(fbUser, { displayName: fullName });
            await fbUser.getIdToken(true); // same token-readiness fix as the fresh-signup branch above
            logRegistrationStage('auth_account_recovered_via_signin', { uid: fbUser.uid, newAccount: false });
          } catch (signInErr) {
            if (signInErr.code === 'auth/wrong-password' || signInErr.code === 'auth/invalid-credential') {
              setError('An account with this email already exists. Please use the correct password, or sign in instead.');
            } else {
              setError(friendlyError(signInErr));
            }
            setLoading(false);
            return;
          }
        } else {
          throw createErr;
        }
      }

      // Product architecture clarification (2026-09-24): PATH A (explicit
      // "Create Firm Account", the founder flow) and PATH B (explicit "Join
      // Firm", shown only when the pre-submission, unauthenticated
      // checkInviteByEmail check already set inviteDetected) are two
      // deliberately separate onboarding paths — a pending-invitation
      // lookup must NEVER be a prerequisite to the founder path. Branching
      // on inviteDetected (already known BEFORE this function even runs,
      // from the same signal that changed the submit button's own label)
      // means the founder path no longer calls checkPendingInvite — the
      // authenticated Firestore query on `invites` — AT ALL, eliminating
      // an entire class of failure for the overwhelming majority of
      // registrations (users who are not invited to anything).
      logRegistrationStage('registration_path_selected', { uid: fbUser.uid, path: inviteDetected ? 'join_existing_firm' : 'create_new_firm' });
      let firmId, role;
      if (inviteDetected) {
        // PATH B — JOIN EXISTING FIRM. We already know (from the pre-
        // submission check) that a pending invite exists for this email —
        // fetching it now that we're authenticated and failing here is
        // therefore genuinely anomalous, not a normal "maybe no invite"
        // branch decision, and must not be silently treated as "no invite".
        // Firm-move lifecycle (2026-10-05): never an arbitrary "first" invite.
        // Exactly one pending invitation → that is the invitation this "Join
        // Firm" registration was shown, so it is accepted; several → the
        // user explicitly chooses one in <PendingInvitations>.
        const invitesResult = await listMyPendingInvites();
        if (!invitesResult.success || invitesResult.pendingCount === 0) {
          console.error('[Registration] expected invite not found/accessible:', invitesResult.code);
          setError('We could not verify your invitation. Please try again in a moment, or contact your administrator.');
          setLoading(false);
          return;
        }
        // C1: the invited address must be verified before the invitation can
        // be redeemed — send the link now and finish on the next sign-in.
        if (!fbUser.emailVerified) {
          logRegistrationStage('invite_email_verification_required', { uid: fbUser.uid });
          await promptInviteEmailVerification(fbUser);
          return;
        }
        if (invitesResult.invites.length > 1) {
          holdRegistration = true;
          setInviteChoice({ invites: invitesResult.invites, fbUser, profile: null, displayName: fullName });
          setLoading(false);
          return;
        }
        // Accepted + profile-provisioned + team-member reconciled, all
        // atomically, via the trusted acceptFirmInvite callable — never a
        // direct client write to users/{uid}.firmId/role/permissions.
        const accepted = await acceptInvitation(invitesResult.invites[0].inviteId, null, fullName);
        if (!accepted.success) {
          console.error('Accept invite error:', accepted.reason || accepted.code);
          if (accepted.reason === 'EMAIL_NOT_VERIFIED') { await promptInviteEmailVerification(fbUser); return; }
          setError(accepted.error || 'Could not join the firm. Please try again or contact your administrator.');
          setLoading(false);
          return;
        }
        firmId = accepted.firmId;
        role = accepted.role;
        logRegistrationStage('invite_accepted', { uid: fbUser.uid, firmId });
      } else {
        // PATH A — CREATE FIRM ACCOUNT (explicit founder path).
        role = 'admin';
        // Incident 2026-11-03: the typing-time "Join Firm" banner is only a hint
        // (it can miss — a stale reply, an early submit). Before minting a new
        // firm, ask the server whether this account's address has a pending
        // invitation (count only for an unverified address — M1). An invited
        // person joins through the invitation, after verifying their email, and
        // never receives a separate firm. A failed lookup is never read as "no
        // invitation": the person is told, and may retry or explicitly choose to
        // create a new firm (genuine founders are not blocked by an outage).
        const preInvites = await listMyPendingInvites();
        if (!preInvites.success && !founderConfirmedNoInvite.current) {
          logRegistrationStage('invite_lookup_failed_founder_paused', { uid: fbUser.uid, code: preInvites.code || null });
          await deliberateSignOut(); // no profile-less session left waiting on the choice
          setInviteCheckFailed(true);
          setLoading(false);
          return;
        }
        setInviteCheckFailed(false);
        if (preInvites.success && preInvites.pendingCount > 0) {
          logRegistrationStage('registration_path_rerouted_to_invite', { uid: fbUser.uid });
          if (!fbUser.emailVerified) { await promptInviteEmailVerification(fbUser); return; }
          holdRegistration = true;
          setInviteChoice({ invites: preInvites.invites, fbUser, profile: null, displayName: fullName });
          setLoading(false);
          return;
        }
        if (!firmName.trim()) {
          setError('Please enter your firm name to create a new account.');
          setLoading(false);
          return;
        }
        // Registration reliability remediation (Araba Chambers / Michael
        // Sarkodie, 2026-09-24): new-firm bootstrap (firm + user profile +
        // founder teamMember) now goes through the trusted bootstrapNewFirm
        // callable instead of three sequential client-side Firestore
        // writes. This removes the entire class of client-SDK auth/token
        // timing risk from the trust-sensitive bootstrap sequence — a
        // Cloud Functions callable's auth is verified synchronously before
        // the handler runs, and every write inside it uses the Admin SDK,
        // which has no client-credential-provider timing concept at all.
        // The callable is itself idempotent (see its own header comment):
        // a retry for the same uid reuses the existing firm rather than
        // minting a second one, so no client-side idempotency pre-check is
        // needed here.
        logRegistrationStage('bootstrap_new_firm_calling', { uid: fbUser.uid });
        try {
          const bootstrapFn = httpsCallable(functions, 'bootstrapNewFirm');
          const data = await callBootstrapNewFirm(bootstrapFn, { firmName: firmName.trim(), fullName });
          firmId = data.firmId;
          role = data.role;
          logRegistrationStage('bootstrap_new_firm_succeeded', { uid: fbUser.uid, firmId, alreadyExisted: !!data.alreadyExisted });
        } catch (bootstrapErr) {
          console.error('[Registration] bootstrapNewFirm failed:', bootstrapErr.code, bootstrapErr.message);
          logRegistrationStage('bootstrap_new_firm_failed', { uid: fbUser.uid, errorCode: bootstrapErr.code || 'unknown' });
          // No sign-out here (unlike the old client-write path's dedicated
          // failure branch): bootstrapNewFirm is atomic — it never leaves
          // partial Firestore state, so there is nothing to strand the user
          // in. A retry safely re-enters this exact same branch.
          setError(bootstrapErr.message || 'We could not set up your account. Please try again — if this keeps happening, contact support.');
          setLoading(false);
          return;
        }
      }
      trackSignUp('email');
      trackFunnelStep('signup_completed');
      logRegistrationStage('registration_completed', { uid: fbUser.uid, firmId, role });
      // Incident 2026-10-21: keep the hold until the gate reaches ACTIVE (profile delivered).
      holdRegistration = true; holdRegistrationUntilActive();
      onLoginSuccess({
        ...fbUser, firmId, role,
        permissions: getPermissionsForRole(role),
        displayName: fullName,
        userProfile: { email: fbUser.email, displayName: fullName, role, firmId }
      });
    } catch (err) {
      console.error('Register error:', err.code, err.message);
      if (err.code === 'auth/email-already-in-use') setError('An account with this email already exists. Try signing in instead.');
      else if (err.code === 'auth/weak-password') setError('Password is too weak. Use at least 8 characters with uppercase, number, and special character.');
      else if (err.code === 'auth/invalid-email') setError('Invalid email address.');
      else if (err.code === 'auth/network-request-failed' || err.code === 'auth/internal-error') {
        const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
          setError('Connection issue on mobile. Try opening firmassist.net directly in Safari or Chrome (not from WhatsApp or social media links).');
        } else {
          setError('Network error — check your internet connection and try again.');
        }
      }
      else setError(friendlyError(err));
    } finally {
      if (!holdRegistration) setRegistrationInProgress(false);
    }
    setLoading(false);
  };

  // =========================================================================
  // MFA: Verify TOTP code during login (user already has MFA enrolled)
  // =========================================================================
  const handleMfaVerify = async () => {
    if (!mfaCode || mfaCode.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setLoading(true);
    setError('');
    try {
      const totpHint = mfaResolver.hints.find(h => h.factorId === 'totp');
      if (!totpHint) { setError('No TOTP factor found. Contact your administrator.'); setLoading(false); return; }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, mfaCode);
      const userCredential = await mfaResolver.resolveSignIn(assertion);
      const fbUser = userCredential.user;
      const profileResult = await getUserProfile(fbUser.uid);
      if (profileResult.success) {
        const profile = profileResult.user;
        onLoginSuccess({
          ...fbUser, firmId: profile.firmId, role: profile.role,
          permissions: profile.permissions,
          displayName: profile.displayName || fbUser.displayName,
          userProfile: profile
        });
      }
      setMfaStep(null);
      setMfaCode('');
    } catch (err) {
      console.error('MFA verify error:', err && err.code); // code only
      if (err.code === 'auth/invalid-verification-code') setError('Invalid code. Check your authenticator app and try again.');
      else setError('Verification failed. Please try again.');
    }
    setLoading(false);
  };

  const getPermissionsForRole = (role) => {
    const perms = {
      admin: { canManageCases: true, canManageClients: true, canManageBilling: true, canManageUsers: true, canViewAnalytics: true, canDeleteRecords: true, canManageDocuments: true, canManageFirmSettings: true },
      lawyer: { canManageCases: true, canManageClients: true, canManageBilling: true, canManageUsers: false, canViewAnalytics: true, canDeleteRecords: false, canManageDocuments: true, canManageFirmSettings: false },
      paralegal: { canManageCases: true, canManageClients: true, canManageBilling: false, canManageUsers: false, canViewAnalytics: false, canDeleteRecords: false, canManageDocuments: true, canManageFirmSettings: false },
      secretary: { canManageCases: false, canManageClients: true, canManageBilling: false, canManageUsers: false, canViewAnalytics: false, canDeleteRecords: false, canManageDocuments: false, canManageFirmSettings: false }
    };
    return perms[role] || perms.lawyer;
  };

  if (inviteChoice) {
    const { invites, fbUser: chooserUser, profile: chooserProfile, displayName: chooserName } = inviteChoice;
    return (
      <div style={{ minHeight: '100vh', background: '#080a10', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '520px', width: '100%' }}>
          <PendingInvitations
            invites={invites}
            currentFirmId={null}
            displayName={chooserName}
            onAccepted={(r) => {
              setInviteChoice(null);
              // Incident 2026-10-21: the new profile may reach the live listener a moment
              // after acceptFirmInvite returns — hold until the gate reaches ACTIVE.
              holdRegistrationUntilActive();
              onLoginSuccess({
                ...chooserUser,
                firmId: r.firmId,
                role: r.role,
                permissions: getPermissionsForRole(r.role),
                displayName: chooserName || chooserUser.displayName,
                userProfile: { ...(chooserProfile || {}), firmId: r.firmId, role: r.role, status: 'active' },
              });
            }}
            onDismiss={async () => {
              setInviteChoice(null);
              // Sign out BEFORE releasing the hold: a profile-less invitee must never
              // be seen as "signed in, no profile, not registering" (PROFILE_MISSING).
              await deliberateSignOut();
              setRegistrationInProgress(false);
              setError('You can accept an invitation the next time you sign in.');
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#080a10',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      {/* Subtle radial glow behind form */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(212,168,83,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .login-panel * { box-sizing: border-box; }
        .login-input {
          width: 100%;
          padding: 0.85rem 0.85rem 0.85rem 2.75rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          color: #e8eaed;
          font-size: 0.95rem;
          outline: none;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.2s, background 0.2s;
        }
        .login-input:focus {
          border-color: rgba(212,168,83,0.5);
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(212,168,83,0.1);
        }
        .login-input::placeholder { color: rgba(255,255,255,0.3); }
        .login-btn {
          width: 100%;
          padding: 0.9rem;
          border: none;
          border-radius: 10px;
          color: #080a10;
          font-size: 1rem;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.2s;
          background: linear-gradient(135deg, #d4a853 0%, #b8862d 100%);
        }
        .login-btn:hover { filter: brightness(1.1); box-shadow: 0 4px 16px rgba(212,168,83,0.3); }
        .login-field { position: relative; margin-bottom: 1rem; }
        .login-field-icon {
          position: absolute; left: 0.85rem; top: 50%; transform: translateY(-50%);
          color: #d4a853; pointer-events: none; display: flex; opacity: 0.7;
        }
        .mfa-code-input {
          width: 100%; padding: 1rem;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px; color: #e8eaed; font-size: 1.5rem; font-weight: 700;
          text-align: center; letter-spacing: 0.5rem; outline: none;
          font-family: 'JetBrains Mono', 'Courier New', monospace; transition: border-color 0.2s;
        }
        .mfa-code-input:focus { border-color: rgba(212,168,83,0.5); box-shadow: 0 0 0 3px rgba(212,168,83,0.1); }
        .mfa-code-input::placeholder { letter-spacing: 0.2rem; font-size: 1rem; font-weight: 400; }
      `}</style>

      <div className="login-panel" style={{
        maxWidth: '440px', width: '100%',
        background: '#0f1219',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px', padding: '2.5rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        position: 'relative', zIndex: 1
      }}>

        {/* ============================================================= */}
        {/* MFA VERIFY — Enter code from authenticator app during login    */}
        {/* ============================================================= */}
        {mfaStep === 'verify' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', margin: '0 auto 1rem',
                background: 'rgba(102,126,234,0.15)', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <Shield size={32} style={{ color: '#d4a853' }} />
              </div>
              <h2 style={{ color: '#e8eaed', fontSize: '1.25rem', margin: '0 0 0.5rem' }}>Two-Factor Authentication</h2>
              <p style={{ color: '#8b8fa3', fontSize: '0.9rem', margin: 0 }}>Enter the 6-digit code from your authenticator app</p>
            </div>

            {error && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.85rem 1rem', marginBottom: '1.25rem',
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: '10px', color: '#ef4444', fontSize: '0.85rem'
              }}>
                <AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <input
                className="mfa-code-input" type="text" inputMode="numeric"
                autoComplete="one-time-code" maxLength={6} placeholder="000000"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter' && mfaCode.length === 6) handleMfaVerify(); }}
                autoFocus
              />
            </div>

            <button onClick={handleMfaVerify} disabled={loading || mfaCode.length !== 6} className="login-btn" style={{
              background: (loading || mfaCode.length !== 6) ? 'rgba(102,126,234,0.3)' : 'linear-gradient(135deg, #d4a853 0%, #b8862d 100%)',
              cursor: (loading || mfaCode.length !== 6) ? 'not-allowed' : 'pointer', marginBottom: '1rem'
            }}>
              {loading ? <><Loader size={18} className="spin" /> Verifying...</> : <>Verify <ArrowRight size={18} /></>}
            </button>

            <button onClick={() => { setMfaStep(null); setMfaCode(''); setMfaResolver(null); setError(''); }} style={{
              background: 'none', border: 'none', color: '#8b8fa3', fontSize: '0.85rem',
              cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'center', padding: '0.5rem'
            }}>
              Back to login
            </button>
          </>
        )}

        {/* ============================================================= */}
        {/* NORMAL LOGIN / REGISTER FORM                                   */}
        {/* ============================================================= */}
        {!mfaStep && (<>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ width: '42px', height: '42px', background: 'linear-gradient(135deg, #d4a853, #b8862d)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Scale size={24} style={{ color: '#080a10' }} />
            </div>
            <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#e8eaed' }}>FirmAssist</span>
          </div>
          <p style={{ color: '#8b8fa3', fontSize: '0.9rem', margin: 0 }}>
            {showResetForm ? 'Reset your password' : isRegister ? 'Create your firm account' : 'Welcome back, Counsel'}
          </p>
        </div>

        {/* ═══════ PASSWORD RESET VIEW ═══════ */}
        {showResetForm ? (
          <>
            {resetSuccess ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.2rem', fontSize: '1.5rem' }}>✅</div>
                <h3 style={{ color: '#e8eaed', margin: '0 0 0.5rem', fontSize: '1.1rem' }}>Check Your Email</h3>
                <p style={{ color: '#a8adb7', fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                  We've sent a password reset link to <strong style={{ color: '#d4a853' }}>{resetEmail}</strong>. Check your inbox (and spam folder) and follow the link to create a new password.
                </p>
                <button onClick={() => { setShowResetForm(false); setResetSuccess(false); setResetEmail(''); setResetError(''); }} className="login-btn" style={{ background: 'linear-gradient(135deg, #d4a853 0%, #b8862d 100%)' }}>
                  Back to Sign In <ArrowRight size={18} />
                </button>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset}>
                <p style={{ color: '#a8adb7', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                  Enter the email address associated with your account and we'll send you a link to reset your password.
                </p>
                {resetError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1rem', marginBottom: '1.25rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '10px', color: '#ef4444', fontSize: '0.85rem' }}>
                    <AlertCircle size={16} style={{ flexShrink: 0 }} />
                    {resetError}
                  </div>
                )}
                <div className="login-field" style={{ marginBottom: '1.5rem' }}>
                  <div className="login-field-icon"><Mail size={18} /></div>
                  <input className="login-input" type="email" placeholder="Email Address" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required />
                </div>
                <button type="submit" disabled={resetLoading} className="login-btn" style={{ background: resetLoading ? 'rgba(245,158,11,0.5)' : 'linear-gradient(135deg, #d4a853 0%, #d97706 100%)', cursor: resetLoading ? 'not-allowed' : 'pointer', color: '#0f0f1e', fontWeight: 700 }}>
                  {resetLoading ? <><Loader size={18} className="spin" /> Sending Reset Link...</> : <>Send Reset Link <ArrowRight size={18} /></>}
                </button>
              </form>
            )}
            {!resetSuccess && (
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <button onClick={() => { setShowResetForm(false); setResetError(''); setResetEmail(''); }} style={{ background: 'none', border: 'none', color: '#d4a853', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>
                  ← Back to Sign In
                </button>
              </div>
            )}
          </>
        ) : (
        <>

        {/* Notice (neutral guidance, e.g. verify your email to join a firm) */}
        {notice && (
          <div role="status" style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.85rem 1rem', marginBottom: '1.25rem',
            background: 'rgba(212,168,83,0.1)',
            border: '1px solid rgba(212,168,83,0.3)',
            borderRadius: '10px', color: '#d4a853', fontSize: '0.85rem'
          }}>
            <Mail size={16} style={{ flexShrink: 0 }} />
            {notice}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.85rem 1rem', marginBottom: '1.25rem',
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '10px', color: '#ef4444', fontSize: '0.85rem'
          }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            {error}
          </div>
        )}

        {/* Founder path: the invitation check could not be completed */}
        {isRegister && inviteCheckFailed && (
          <div role="alert" style={{
            padding: '0.85rem 1rem', marginBottom: '1.25rem',
            background: 'rgba(212,168,83,0.1)', border: '1px solid rgba(212,168,83,0.3)',
            borderRadius: '10px', color: '#d4a853', fontSize: '0.85rem', lineHeight: 1.5
          }}>
            <div style={{ marginBottom: '0.75rem' }}>
              FirmAssist couldn't check whether {email || 'this email address'} has been invited to an existing firm. If your firm added you, check again — creating a new firm will not give you access to theirs.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" disabled={loading} onClick={() => { setInviteCheckFailed(false); founderConfirmedNoInvite.current = false; handleRegister({ preventDefault() {} }); }}
                style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#e8eaed', cursor: 'pointer', fontFamily: 'inherit' }}>
                Check again
              </button>
              <button type="button" disabled={loading} onClick={() => { setInviteCheckFailed(false); founderConfirmedNoInvite.current = true; handleRegister({ preventDefault() {} }); }}
                style={{ padding: '0.5rem 1rem', background: 'none', border: '1px solid rgba(212,168,83,0.4)', borderRadius: '8px', color: '#d4a853', cursor: 'pointer', fontFamily: 'inherit' }}>
                I wasn't invited — create a new firm
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={isRegister ? handleRegister : handleLogin}>
          {isRegister && (
            <>
              {!inviteDetected && (
                <div className="login-field">
                  <div className="login-field-icon"><Building size={18} /></div>
                  <input className="login-input" type="text" placeholder="Firm Name (e.g. Mensah & Associates)" value={firmName} onChange={(e) => setFirmName(e.target.value)} />
                </div>
              )}
              {inviteDetected && (
                <div style={{
                  padding: '0.85rem 1rem', marginBottom: '1rem',
                  background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.25)',
                  borderRadius: '10px', color: '#10b981', fontSize: '0.85rem'
                }}>
                  ✅ You've been invited to join as <strong style={{ textTransform: 'capitalize' }}>{inviteDetected.role}</strong>. Just complete your registration below.
                </div>
              )}
              <div className="login-field">
                <div className="login-field-icon"><User size={18} /></div>
                <input className="login-input" type="text" placeholder="Your Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </div>
            </>
          )}

          <div className="login-field">
            <div className="login-field-icon"><Mail size={18} /></div>
            <input className="login-input" type="email" placeholder="Email Address" value={email} onChange={(e) => { setEmail(e.target.value); setInviteCheckFailed(false); founderConfirmedNoInvite.current = false; if (isRegister) checkInviteForEmail(e.target.value); }} required />
            {checkingInvite && <div style={{ position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#d4a853' }}><Loader size={16} className="spin" /></div>}
          </div>

          <div className="login-field" style={{ marginBottom: isRegister ? '0.25rem' : '0.5rem' }}>
            <div className="login-field-icon"><Lock size={18} /></div>
            <input className="login-input" type={showPassword ? 'text' : 'password'} placeholder={isRegister ? "Password (8+ chars, upper, number, symbol)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} required style={{ paddingRight: '2.75rem' }} />
            <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
              position: 'absolute', right: '0.85rem', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', color: '#8b8fa3', cursor: 'pointer', padding: 0, display: 'flex'
            }}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {!isRegister && (
            <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
              <button type="button" onClick={() => { setShowResetForm(true); setResetEmail(email); setResetError(''); setResetSuccess(false); }} style={{ background: 'none', border: 'none', color: '#d4a853', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, padding: 0 }}>
                Forgot Password?
              </button>
            </div>
          )}
          {isRegister && password && (
            <div style={{ marginBottom: '0.75rem', fontSize: '0.75rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingLeft: '0.25rem' }}>
              <span style={{ color: password.length >= 8 ? '#10b981' : '#6b7280' }}>{password.length >= 8 ? '✓' : '○'} 8+ chars</span>
              <span style={{ color: /[A-Z]/.test(password) ? '#10b981' : '#6b7280' }}>{/[A-Z]/.test(password) ? '✓' : '○'} Uppercase</span>
              <span style={{ color: /[0-9]/.test(password) ? '#10b981' : '#6b7280' }}>{/[0-9]/.test(password) ? '✓' : '○'} Number</span>
              <span style={{ color: /[^A-Za-z0-9]/.test(password) ? '#10b981' : '#6b7280' }}>{/[^A-Za-z0-9]/.test(password) ? '✓' : '○'} Symbol</span>
            </div>
          )}

          {isRegister && (
            <div className="login-field" style={{ marginBottom: '1.5rem' }}>
              <div className="login-field-icon"><Lock size={18} /></div>
              <input className="login-input" type={showPassword ? 'text' : 'password'} placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
            </div>
          )}

          <button type="submit" disabled={loading} className="login-btn" style={{
            background: loading ? 'rgba(102,126,234,0.5)' : 'linear-gradient(135deg, #d4a853 0%, #b8862d 100%)',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}>
            {loading ? (
              <><Loader size={18} className="spin" /> {isRegister ? 'Creating Account...' : 'Signing In...'}</>
            ) : (
              <>{isRegister ? (inviteDetected ? 'Join Firm' : 'Create Firm Account') : 'Sign In'} <ArrowRight size={18} /></>
            )}
          </button>
        </form>

        {/* Toggle */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <span style={{ color: '#8b8fa3', fontSize: '0.9rem' }}>
            {isRegister ? 'Already have an account? ' : "Don't have an account? "}
          </span>
          <button onClick={() => { setIsRegister(!isRegister); setError(''); }} style={{
            background: 'none', border: 'none', color: '#d4a853',
            fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit'
          }}>
            {isRegister ? 'Sign In' : 'Register Your Firm'}
          </button>
        </div>

        {isRegister && (
          <div style={{
            marginTop: '1.25rem', padding: '1rem',
            background: 'rgba(102,126,234,0.08)',
            border: '1px solid rgba(102,126,234,0.15)',
            borderRadius: '10px', fontSize: '0.8rem', color: '#a8adb7', lineHeight: 1.5
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', color: '#d4a853', fontWeight: 600 }}>
              <Users size={14} /> Multi-User Support
            </div>
            {inviteDetected
              ? 'You have a pending invitation. Complete your registration to join the firm.'
              : "Registering creates your firm account. You'll be the admin and can invite lawyers, paralegals, and staff from within the app."
            }
          </div>
        )}
        </>
        )}
        </>)}
      </div>
    </div>
  );
};

export default Login;
